import { Grid, Paper } from "@mui/material";
import { CandidatesAddedReport } from "../../../Reports/CandidatesAdded/CandidatesAddedReport";
import { CandidatesAddedByCampaignReport } from "../../../Reports/CandidatesAddedByCampaign/CandidatesAddedByCampaignReport";
import AttributeProtectedComponent from "../../../App/Components/ABAC/AttributeProtectedComponent";
import { AppFeatureLookup } from "../../../App/Constants/AbacConstants";
import { EmailPerformanceReport } from "../../../Reports/EmailPerformance/EmailPerformance";
import { useEmailPerformanceReportState } from "../../../Reports/EmailPerformance/EmailPerformanceReportState";
import { TextPerformanceReport } from "../../../Reports/TextPerformance/TextPerformance";
import { useTextPerformanceReportState } from "../../../Reports/TextPerformance/TextPerformanceReportState";
import EmailMetricCards from "../../../Reports/EmailPerformance/EmailMetricsCards";
import TextMetricCards from "../../../Reports/TextPerformance/TextMetricsCards";
import {
  ICandidatesAddedReportState,
  useCandidatesAddedReportState,
} from "../../../Reports/CandidatesAdded/State/CandidatesAddedReportState";
import MetricCard from "../../../App/Components/MetricCard/MetricCard";
import { IndividualEmailPerformance } from "../../../Reports/EmailPerformance/IndividualEmailPerformance";
import { IndividualTextPerformance } from "../../../Reports/TextPerformance/IndividualTextPerformance";
import { useCandidatesAddedByCampaignReportState } from "../../../Reports/CandidatesAddedByCampaign/State/CandidatesAddedByCampaignReportState";

export const Reports = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
  const candidatesState = useCandidatesAddedReportState(startDate, endDate);
  const emailState = useEmailPerformanceReportState(startDate, endDate);
  const textState = useTextPerformanceReportState(startDate, endDate);
  const candidatesAddedCampaignState = useCandidatesAddedByCampaignReportState(startDate, endDate);

  return (
    <>
      <Grid container>
        <Grid item xs={12} sx={{ mb: 2 }}>
          <AttributeProtectedComponent featureName={AppFeatureLookup.DashboardCandidatesAddedReport}>
            <CandidatesMetricCards state={candidatesState} />
          </AttributeProtectedComponent>
        </Grid>
        <Grid item xs={12} lg={6} sx={{ mb: 2, pr: { xs: 0, lg: 2 } }}>
          <AttributeProtectedComponent featureName={AppFeatureLookup.DashboardCandidatesAddedReport}>
            <ReportWrapper>
              <CandidatesAddedReport state={candidatesState} />
            </ReportWrapper>
          </AttributeProtectedComponent>
        </Grid>
        <Grid item xs={12} lg={6} sx={{ mb: 2 }}>
          <AttributeProtectedComponent featureName={AppFeatureLookup.DashboardCandidatesByCampaignReport}>
            <ReportWrapper>
              <CandidatesAddedByCampaignReport state={candidatesAddedCampaignState} />
            </ReportWrapper>
          </AttributeProtectedComponent>
        </Grid>
        <Grid item xs={12}>
          <EmailMetricCards id="dashboard" state={emailState} />
        </Grid>
        <Grid item xs={12} lg={6} sx={{ mb: 2, pr: { xs: 0, lg: 2 } }}>
          <Paper sx={{ height: "100%", p: { xs: 2, lg: 4 } }}>
            <IndividualEmailPerformance
              hideEditColumns={true}
              defaultPageSize={3}
              startDate={startDate}
              endDate={endDate}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} lg={6} sx={{ mb: 2 }}>
          <ReportWrapper>
            <EmailPerformanceReport
              reportData={emailState.getters.reportData}
              enabled={true}
              id="dashboard"
              loading={emailState.getters.isLoading}
            />
          </ReportWrapper>
        </Grid>
        <Grid item xs={12}>
          <TextMetricCards id="dashboard" state={textState} />
        </Grid>
        <Grid item xs={12} lg={6} sx={{ mb: 2, pr: { xs: 0, lg: 2 } }}>
          <Paper sx={{ height: "100%", p: { xs: 2, lg: 4 } }}>
            <IndividualTextPerformance
              hideEditColumns={true}
              defaultPageSize={3}
              startDate={startDate}
              endDate={endDate}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} lg={6} sx={{ mb: 2 }}>
          <ReportWrapper>
            <TextPerformanceReport
              reportData={textState.getters.reportData}
              enabled={true}
              id="dashboard"
              loading={textState.getters.isLoading}
            />
          </ReportWrapper>
        </Grid>
      </Grid>
    </>
  );
};

function CandidatesMetricCards({ state }: { state: ICandidatesAddedReportState }) {
  const { reportData, previousCount, percentChange, prevStartDate, prevEndDate, isLoadingPrevReport } = state;

  return (
    <MetricCard
      id="campaign-candidate-captures"
      title="Candidate Captures"
      data={reportData.reduce((a, b) => a + b.candidateCount, 0)}
      prevData={previousCount}
      percentChange={percentChange}
      prevStartDate={prevStartDate}
      prevEndDate={prevEndDate}
      isLoadingPrevReport={isLoadingPrevReport}
    />
  );
}

function ReportWrapper({ children }: { children: string | JSX.Element | JSX.Element[] }) {
  return (
    <Paper
      sx={{
        height: "100%",
        p: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {children}
    </Paper>
  );
}
